import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, Square, Sparkles, Loader2, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { SpamFolderNote } from './SpamFolderTip';
import { useToast } from '../../hooks/use-toast';
import { useMicRecorder } from '../voice/useMicRecorder';
import { transcribeBlob } from '../voice/transcribe';
import { MicCheck } from '../voice/MicCheck';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } });

export const useVoice = (onText) => {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const mic = useMicRecorder({
    onBlob: async (blob) => {
      setBusy(true);
      setFailed('');
      try {
        const said = (await transcribeBlob(blob, 'message')).trim();
        if (said) onText(said); else setFailed("We couldn't make out any words. Speak closer to the microphone and try again.");
      } catch (e) { setFailed(e.response?.data?.detail || 'Transcription failed. Please try again or type instead.'); } finally { setBusy(false); }
    },
  });
  return { ...mic, error: mic.error || failed, busy, start: () => { setFailed(''); mic.start('record'); } };
};

const PatientPicker = ({ value, onChange }) => {
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND}/api/practice/patients`, auth()).then((r) => setList(r.data?.data || r.data?.patients || [])).catch(() => setList([]));
  }, []);
  const shown = list.filter((p) => `${p.firstName} ${p.lastName}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  if (value) return null;
  return (
    <div className="space-y-2" data-testid="compose-patient-picker">
      <Input placeholder="Search patient…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="compose-patient-search" />
      <div className="max-h-48 overflow-y-auto divide-y border rounded-lg">
        {shown.map((p) => (
          <button key={p.id} type="button" onClick={() => onChange(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`compose-patient-${p.id}`}>
            <b>{p.firstName} {p.lastName}</b> <span className="text-slate-500">{[p.phone, p.email].filter(Boolean).join(' · ')}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ComposeMessageDialog = ({ open, onOpenChange, patient: initialPatient, onSent }) => {
  const { toast } = useToast();
  const [patient, setPatient] = useState(initialPatient || null);
  const [instruction, setInstruction] = useState('');
  const [channel, setChannel] = useState('sms');
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState('');
  const voice = useVoice((t) => setInstruction((s) => (s ? `${s} ${t}` : t)));

  useEffect(() => { if (open) { setPatient(initialPatient || null); setInstruction(''); setDraft(null); setChannel('sms'); } }, [open, initialPatient]);

  const write = async () => {
    setBusy('write');
    try {
      const r = await axios.post(`${BACKEND}/api/messages/compose`, { patientId: patient.id, instruction, channel }, auth());
      setDraft(r.data.draft);
      setMeta({ language: r.data.language, canSms: r.data.canSms, canEmail: r.data.canEmail });
      if (!r.data.canSms && r.data.canEmail) setChannel('email');
    } catch (e) { toast({ title: 'AI could not write the message', description: e.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    setBusy('');
  };

  const send = async (asWritten = false) => {
    setBusy(asWritten ? 'direct' : 'send');
    const text = instruction.trim();
    const body = asWritten
      ? { sms: text, emailSubject: '', emailBody: text, aiGenerated: false }
      : { sms: draft.sms, emailSubject: draft.emailSubject, emailBody: draft.emailBody, aiGenerated: true };
    try {
      const r = await axios.post(`${BACKEND}/api/messages/send`, { patientId: patient.id, channel, ...body }, auth());
      toast({ title: 'Sent', description: `Sent by ${r.data.sent.join(' + ')}${r.data.warnings?.length ? ` (${r.data.warnings.join('; ')})` : ''}` });
      onOpenChange(false);
      onSent?.(patient);
    } catch (e) { toast({ title: 'Send failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' }); }
    setBusy('');
  };

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="compose-message-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-sky-600" /> Message Patient</DialogTitle></DialogHeader>
        <PatientPicker value={patient} onChange={setPatient} />
        {patient && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold" data-testid="compose-patient-name">To: {patient.firstName} {patient.lastName}</span>
              {!initialPatient && <button className="text-sky-700 underline text-xs" onClick={() => { setPatient(null); setDraft(null); }}>change</button>}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">What do you want to tell them? (speak or type)</label>
              <div className="flex gap-2">
                <Textarea rows={3} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder='e.g. "Crown is in, call to schedule. He can eat normally today."' data-testid="compose-instruction-input" />
                <Button type="button" variant={voice.recording ? 'destructive' : 'outline'} onClick={voice.recording ? voice.stop : voice.start} disabled={voice.busy} className="h-auto" data-testid="compose-voice-btn">
                  {voice.busy ? <Loader2 className="h-5 w-5 animate-spin" /> : voice.recording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
              </div>
              <MicCheck mic={voice} prefix="compose" />
            </div>
            <div className="flex gap-2" data-testid="compose-channel">
              {[['sms', 'Text'], ['email', 'Email'], ['both', 'Both']].map(([k, l]) => (
                <Button key={k} type="button" size="sm" variant={channel === k ? 'default' : 'outline'} onClick={() => setChannel(k)} data-testid={`compose-channel-${k}`}>{l}</Button>
              ))}
              <Button className="ml-auto" variant={draft ? 'default' : 'outline'} onClick={write} disabled={!instruction.trim() || !!busy} data-testid="compose-write-btn">
                {busy === 'write' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} {draft ? 'Rewrite with AI' : 'Write with AI'}
              </Button>
              {!draft && (
                <Button onClick={() => send(true)} disabled={!instruction.trim() || !!busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="compose-send-direct-btn">
                  {busy === 'direct' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Send as written
                </Button>
              )}
            </div>
            {!draft && <p className="text-xs text-slate-500 -mt-2">Send your words exactly as typed, or let AI polish them first and review before sending.</p>}
            {channel !== 'email' && <SpamFolderNote />}
            {draft && (
              <div className="space-y-3 border-t pt-4" data-testid="compose-draft">
                {meta.language && meta.language !== 'English' && <p className="text-xs text-sky-700">Written in {meta.language} (patient's language).</p>}
                {channel !== 'email' && (
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase text-slate-500">Text message {!meta.canSms && <span className="text-rose-600 normal-case">— no cell phone on file</span>}</label>
                    <Textarea rows={3} value={draft.sms || ''} onChange={set('sms')} data-testid="compose-sms-input" />
                    <p className="text-[11px] text-slate-400">{(draft.sms || '').length} characters</p>
                    {meta.language !== 'English' && draft.englishSms && <p className="text-xs text-slate-500">English: {draft.englishSms}</p>}
                  </div>
                )}
                {channel !== 'sms' && (
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase text-slate-500">Email {!meta.canEmail && <span className="text-rose-600 normal-case">— no email on file</span>}</label>
                    <Input value={draft.emailSubject || ''} onChange={set('emailSubject')} data-testid="compose-email-subject" />
                    <Textarea rows={6} value={draft.emailBody || ''} onChange={set('emailBody')} data-testid="compose-email-body" />
                  </div>
                )}
                <Button onClick={() => send(false)} disabled={!!busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="compose-send-btn">
                  {busy === 'send' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Review complete — Send
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComposeMessageDialog;
